import BiologicalModelPage from '../../Components/Biological/BiologicalModelPage';
import { lotkaVolterra1D, lotkaVolterra2D, lotkaVolterraStochastic } from './simulations/lotkaVolterra';
import { fisher1D, fisher2D, fisherStochastic } from './simulations/fisher';
import { fhn0D, fhn2D, fhnStochastic } from './simulations/fitzHughNagumo';
import { hhSingleSpike, hhSpikeTrain, hhStochastic } from './simulations/hodgkinHuxley';

const lvCommonParams = [
    { key: 'prey0', label: 'Initial prey', default: 10, min: 1, max: 20, step: 0.5 },
    { key: 'predator0', label: 'Initial predator', default: 5, min: 1, max: 15, step: 0.5 },
    { key: 'alpha', label: 'Prey growth (alpha)', default: 1.1, min: 0.2, max: 2, step: 0.1 },
    { key: 'beta', label: 'Predation (beta)', default: 0.4, min: 0.1, max: 1, step: 0.05 },
    { key: 'delta', label: 'Predator gain (delta)', default: 0.1, min: 0.05, max: 0.5, step: 0.01 },
    { key: 'gamma', label: 'Predator decay (gamma)', default: 0.4, min: 0.1, max: 1, step: 0.05 },
];

export function LotkaVolterraPage() {
    return (
        <BiologicalModelPage
            title="Lotka-Volterra System"
            subtitle="Predator-prey dynamics across ODE, spatial, and stochastic regimes"
            description="The Lotka-Volterra model captures oscillatory coexistence between prey and predator populations. Explore the classical closed-orbit ODE, a 2D reaction-diffusion field producing swirling population waves, and a stochastic SDE with multiplicative environmental noise."
            variants={[
                {
                    key: '1d',
                    label: '1D (ODE Phase Plot)',
                    equations: [
                        '\\frac{dx}{dt} = \\alpha x - \\beta x y',
                        '\\frac{dy}{dt} = \\delta x y - \\gamma y',
                    ],
                    description: 'Closed orbits in phase space emerge from neutrally stable oscillations between prey ($x$) and predator ($y$) populations.',
                    simulate: lotkaVolterra1D,
                    parameters: lvCommonParams,
                },
                {
                    key: '2d',
                    label: '2D (Spatial Waves)',
                    equations: [
                        '\\frac{\\partial x}{\\partial t} = \\alpha x - \\beta x y + D\\nabla^2 x',
                        '\\frac{\\partial y}{\\partial t} = \\delta x y - \\gamma y + D\\nabla^2 y',
                    ],
                    description: 'Diffusive coupling turns local oscillations into traveling and spiral population waves across a 2D habitat.',
                    simulate: lotkaVolterra2D,
                    canvasSize: { width: 540, height: 540 },
                    parameters: [
                        ...lvCommonParams.filter((p) => !['prey0', 'predator0'].includes(p.key)),
                        { key: 'diffusion2d', label: 'Spatial diffusion D', default: 0.15, min: 0.02, max: 0.4, step: 0.01 },
                    ],
                },
                {
                    key: 'stochastic',
                    label: 'Stochastic (SDE)',
                    equations: [
                        'dx = (\\alpha x - \\beta x y)\\,dt + \\sigma_1 x\\,dW_1',
                        'dy = (\\delta x y - \\gamma y)\\,dt + \\sigma_2 y\\,dW_2',
                    ],
                    description: 'Euler-Maruyama integration of the stochastic Lotka-Volterra SDE with multiplicative noise. The full phase trajectory is retained, revealing noise-broadened orbits and occasional extinction near the origin.',
                    simulate: lotkaVolterraStochastic,
                    parameters: [
                        ...lvCommonParams,
                        { key: 'noisePrey', label: 'Noise sigma_1 (prey)', default: 0.12, min: 0, max: 0.5, step: 0.01 },
                        { key: 'noisePredator', label: 'Noise sigma_2 (predator)', default: 0.12, min: 0, max: 0.5, step: 0.01 },
                    ],
                },
            ]}
        />
    );
}

const fisherCommonParams = [
    { key: 'diffusion', label: 'Diffusion D', default: 0.3, min: 0.05, max: 1, step: 0.05 },
    { key: 'growth', label: 'Growth rate r', default: 1.0, min: 0.1, max: 3, step: 0.1 },
    { key: 'dt', label: 'Time step', default: 0.15, min: 0.02, max: 0.3, step: 0.01 },
];

export function FisherEquationPage() {
    return (
        <BiologicalModelPage
            title="Fisher Equation"
            subtitle="Reaction-diffusion front propagation across 1D, 2D, and stochastic media"
            description="The Fisher-KPP equation models spatial spread of a population with logistic growth. Traveling fronts propagate at a minimal speed set by diffusion and growth rate; in 2D the front becomes an expanding, texture-rich colonization ring."
            variants={[
                {
                    key: '1d',
                    label: '1D (Traveling Front)',
                    equations: ['\\frac{\\partial u}{\\partial t} = D\\frac{\\partial^2 u}{\\partial x^2} + r u (1 - u)'],
                    description: 'Minimal wave speed: $c_{\\min} = 2\\sqrt{Dr}$.',
                    simulate: fisher1D,
                    parameters: fisherCommonParams,
                },
                {
                    key: '2d',
                    label: '2D (Expanding Front)',
                    equations: ['\\frac{\\partial u}{\\partial t} = D\\nabla^2 u + r u (1 - u)'],
                    description: 'A localized seed grows into a colorful, radially expanding colonization front with mesmerizing edge texture.',
                    simulate: fisher2D,
                    canvasSize: { width: 540, height: 540 },
                    parameters: fisherCommonParams,
                },
                {
                    key: 'stochastic',
                    label: 'Stochastic (Rough Interface)',
                    equations: ['\\frac{\\partial u}{\\partial t} = D\\frac{\\partial^2 u}{\\partial x^2} + r u (1-u) + \\sigma \\sqrt{u(1-u)}\\,\\xi(x,t)'],
                    description: 'Multiplicative spatial noise $\\xi(x,t)$ roughens the propagating interface, illustrating stochastic front wandering.',
                    simulate: fisherStochastic,
                    parameters: [...fisherCommonParams, { key: 'noise', label: 'Noise sigma', default: 0.08, min: 0, max: 0.3, step: 0.01 }],
                },
            ]}
        />
    );
}

const fhnCommonParams = [
    { key: 'stimulus', label: 'Stimulus I', default: 0.5, min: 0, max: 2, step: 0.1 },
    { key: 'a', label: 'Parameter a', default: 0.7, min: 0.1, max: 1.5, step: 0.05 },
    { key: 'b', label: 'Parameter b', default: 0.8, min: 0.1, max: 1.5, step: 0.05 },
    { key: 'tau', label: 'Recovery tau', default: 12.5, min: 2, max: 20, step: 0.5 },
];

export function FitzHughNagumoPage() {
    return (
        <BiologicalModelPage
            title="FitzHugh-Nagumo Model"
            subtitle="Excitable neuron dynamics across single-cell, spatial, and noisy regimes"
            description="The FitzHugh-Nagumo system is a two-variable reduction of Hodgkin-Huxley dynamics. It produces threshold-triggered spikes, spiral excitation waves in 2D media, and noise-driven coherence resonance."
            variants={[
                {
                    key: '0d',
                    label: 'Single Cell (Time Trace)',
                    equations: [
                        '\\frac{dv}{dt} = v - \\frac{v^3}{3} - w + I',
                        '\\frac{dw}{dt} = \\frac{v + a - b w}{\\tau}',
                    ],
                    description: 'Fast voltage $v$ and slow recovery $w$ interact to produce threshold-triggered action-potential-like spikes.',
                    simulate: fhn0D,
                    parameters: fhnCommonParams,
                },
                {
                    key: '2d',
                    label: '2D (Spiral Waves)',
                    equations: [
                        '\\frac{\\partial v}{\\partial t} = v - \\frac{v^3}{3} - w + I + D\\nabla^2 v',
                        '\\frac{\\partial w}{\\partial t} = \\frac{v + a - b w}{\\tau}',
                    ],
                    description: 'Diffusive coupling between excitable cells produces mesmerizing rotating spiral waves, a hallmark of cardiac and neural tissue models.',
                    simulate: fhn2D,
                    canvasSize: { width: 540, height: 540 },
                    parameters: [...fhnCommonParams, { key: 'diffusion2d', label: 'Spatial diffusion D', default: 0.1, min: 0.02, max: 0.3, step: 0.01 }],
                },
                {
                    key: 'stochastic',
                    label: 'Stochastic (Noise-Induced Spiking)',
                    equations: ['\\frac{dv}{dt} = v - \\frac{v^3}{3} - w + I + \\eta(t)'],
                    description: 'Additive noise $\\eta(t)$ can trigger spontaneous spikes even below threshold, a phenomenon known as coherence resonance.',
                    simulate: fhnStochastic,
                    parameters: [...fhnCommonParams, { key: 'noise', label: 'Noise amplitude', default: 0.3, min: 0, max: 1, step: 0.05 }],
                },
            ]}
        />
    );
}

const hhCommonParams = [
    { key: 'gNa', label: 'g_Na', default: 120, min: 50, max: 200, step: 5 },
    { key: 'gK', label: 'g_K', default: 36, min: 10, max: 80, step: 2 },
    { key: 'gL', label: 'g_L', default: 0.3, min: 0.1, max: 1, step: 0.05 },
];

export function HodgkinHuxleyPage() {
    return (
        <BiologicalModelPage
            title="Hodgkin-Huxley Model"
            subtitle="Ion-channel driven action potentials: single spikes, trains, and noisy firing"
            description="The Hodgkin-Huxley model describes membrane voltage via sodium and potassium conductances with gating variables m, h, and n. Explore a single triggered spike, sustained repetitive spike trains, and channel-noise-driven irregular firing."
            variants={[
                {
                    key: 'single',
                    label: 'Single Spike',
                    equations: [
                        'C_m \\frac{dV}{dt} = I - g_{Na} m^3 h (V - E_{Na}) - g_K n^4 (V - E_K) - g_L (V - E_L)',
                    ],
                    description: 'A brief threshold-crossing current pulse triggers one all-or-none action potential followed by refractory recovery.',
                    simulate: hhSingleSpike,
                    parameters: [{ key: 'current', label: 'Injected current (brief)', default: 8, min: 0, max: 20, step: 0.5 }, ...hhCommonParams],
                },
                {
                    key: 'train',
                    label: 'Spike Train (Repetitive Firing)',
                    equations: [
                        'C_m \\frac{dV}{dt} = I_{\\mathrm{sustained}} - g_{Na} m^3 h (V - E_{Na}) - g_K n^4 (V - E_K) - g_L (V - E_L)',
                    ],
                    description: 'Sustained suprathreshold current drives periodic spiking; spike count and inter-spike interval depend on current amplitude.',
                    simulate: hhSpikeTrain,
                    parameters: [{ key: 'current', label: 'Sustained current', default: 12, min: 0, max: 30, step: 0.5 }, ...hhCommonParams],
                },
                {
                    key: 'stochastic',
                    label: 'Stochastic (Channel Noise)',
                    equations: ['I_{\\mathrm{eff}}(t) = I + \\sigma\\,\\xi(t)'],
                    description: 'Random fluctuations in effective drive current model channel noise, producing irregular inter-spike intervals characteristic of biological neurons.',
                    simulate: hhStochastic,
                    parameters: [
                        { key: 'current', label: 'Base current', default: 10, min: 0, max: 25, step: 0.5 },
                        { key: 'noise', label: 'Noise sigma', default: 6, min: 0, max: 15, step: 0.5 },
                        ...hhCommonParams,
                    ],
                },
            ]}
        />
    );
}

export {
    SegregationPage,
    EpidemicPage,
    FlockingPage,
} from './models/zooPages';
